import { motion } from "framer-motion";
import { Globe2, Users, CalendarCheck2 } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const CHAPTERS = [
  {
    num: "01",
    icon: CalendarCheck2,
    title: "Established 2023",
    text: "Born in Angamaly with one promise — a fresh, dust-free home for every family. Today we serve Angamaly, Chalakudy, Perumbavoor, Koratty and surrounding major regions.",
  },
  {
    num: "02",
    icon: Users,
    title: "Trained Professional Teams",
    text: "Systematic, careful and fully equipped crews for homes, villas, flats and offices — every member trained to treat your property like their own.",
  },
  {
    num: "03",
    icon: Globe2,
    title: "Expat & Premium Home Care",
    text: "Miles away? We are your eyes on the ground. Key-handover management, photo & video proof after every clean, and periodic care plans for your Kerala home.",
  },
];

export default function About() {
  return (
    <section id="about" className="relative py-24 lg:py-36 overflow-hidden" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 grid lg:grid-cols-12 gap-14 lg:gap-16 items-center">
        <div className="lg:col-span-5 relative order-2 lg:order-1">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease: EASE }}
            className="relative"
          >
            <div className="absolute -inset-4 rounded-[2.4rem] bg-gradient-to-br from-[#6CBE45]/15 via-transparent to-[#D4AF37]/20 blur-xl" />
            <div className="gold-frame relative overflow-hidden rounded-[2rem] shadow-[0_30px_70px_-20px_rgba(74,143,42,0.35)]">
              <img
                src="/assets/about-team.jpg"
                alt="PK Hygiene team at work with branded service vehicle"
                className="w-full h-[480px] sm:h-[560px] object-cover hover:scale-105 transition-transform duration-[1200ms] ease-out"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F1A1C]/45 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                <div>
                  <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#82CB7E]">
                    Team PK on site
                  </p>
                  <p className="text-white font-bold text-lg leading-tight mt-1">
                    Fully equipped. Fully accountable.
                  </p>
                </div>
              </div>
            </div>
            <div className="float-slow absolute -top-7 -right-4 sm:-right-7 bg-[#0F1A1C] text-white rounded-2xl px-5 py-4 shadow-xl">
              <p className="font-display text-3xl font-extrabold text-[#D4AF37]">2023</p>
              <p className="text-[11px] font-mono-accent uppercase tracking-[0.18em] text-white/70">
                Est. Angamaly
              </p>
            </div>
          </motion.div>
        </div>

        <div className="lg:col-span-7 order-1 lg:order-2">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: EASE }}
            className="font-mono-accent text-xs font-semibold uppercase tracking-[0.28em] text-[#6CBE45] mb-5"
          >
            About PK Hygiene
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.85, delay: 0.1, ease: EASE }}
            className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.08]"
          >
            Cleaning, elevated to a{" "}
            <span className="font-serif-accent italic font-semibold text-[#4A8F2A]">
              craft
            </span>
            <span className="text-[#D4AF37]">.</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.18, ease: EASE }}
            className="mt-6 font-mono-accent text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] text-[#4A8F2A]"
            data-testid="about-subheader"
          >
            Trustworthy • Reliable • Budget-Friendly Deep Cleaning Services
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.26, ease: EASE }}
            className="mt-5 text-base sm:text-lg text-[#4A5A5E] leading-relaxed max-w-2xl"
            data-testid="about-intro-text"
          >
            PK Hygiene House Cleaning Services provides professional deep
            cleaning solutions for homes, flats, offices, villas, and
            commercial spaces. Whether it&apos;s a newly occupied property, a
            regularly used home, or a space that has been closed for a long
            time, our professional team is committed to bringing back
            cleanliness, freshness, and hygiene. With a sincere and
            professional approach, Team PK focuses on quality work and
            customer satisfaction.
          </motion.p>

          <div className="mt-12 space-y-0">
            {CHAPTERS.map((c, i) => (
              <motion.div
                key={c.num}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: i * 0.12, ease: EASE }}
                className="group flex gap-6 sm:gap-8 py-7 border-t border-[#6CBE45]/12 last:border-b hover:bg-white/70 transition-colors duration-500 px-2 sm:px-4 -mx-2 sm:-mx-4 rounded-lg"
                data-testid={`about-chapter-${c.num}`}
              >
                <span className="font-mono-accent text-sm font-semibold text-[#D4AF37] pt-1.5 shrink-0">
                  {c.num}
                </span>
                <div>
                  <h3 className="flex items-center gap-3 text-xl sm:text-2xl font-semibold tracking-tight text-[#4A8F2A]">
                    <c.icon className="w-5 h-5 text-[#6CBE45] group-hover:text-[#D4AF37] transition-colors duration-300" />
                    {c.title}
                  </h3>
                  <p className="mt-2.5 text-[#4A5A5E] leading-relaxed max-w-xl text-sm sm:text-base">
                    {c.text}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
